import React, { useRef } from "react"
import DynamicForm from "../auth/dynamicForm"
import { Dialog } from "@headlessui/react"
import { XIcon } from "@heroicons/react/outline"
import { navigate } from "gatsby"
import ReCAPTCHA from "react-google-recaptcha"
import RichText from "../../utils/richText"

export default function FormModal({ isOpen, setIsOpen, form }) {
	const [isSubmitting, setIsSubmitting] = React.useState(false)
	const reRef = useRef(null)

	const handleSubmit = async (values) => {
		setIsSubmitting(true)

		try {
			const token = await reRef.current.executeAsync()
			reRef.current.reset()

			// Get referral info from localStorage
			let referralInfo = null
			if (typeof window !== "undefined") {
				const storedInfo = localStorage.getItem("referral_info")
				if (storedInfo) {
					referralInfo = JSON.parse(storedInfo)
				}
			}

			const organizationFields = {
				name: values.organizationName,
				type: values.organizationType,
				industry: values.industry,
				size: values.organizationSize,
				phone: values.phone,
				country: values.country,
			}

			// Only include fields that exist in the form values
			const organization = Object.fromEntries(
				Object.entries(organizationFields).filter(([_, value]) => value !== undefined)
			)

			const userFields = {
				firstName: values.firstName,
				lastName: values.lastName,
				email: values.email,
			}

			// Only include user fields that exist
			const user = Object.fromEntries(
				Object.entries(userFields).filter(([_, value]) => value !== undefined)
			)

			const activityObject = {
				action: "lead-capture",
				data: {
					...user,
					organization: Object.keys(organization).length > 0 ? organization : undefined,
					tag: form.tag,
					...(referralInfo && { referral: referralInfo }),
				},
			}

			const response = await fetch("/api/submit/lead-capture", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					...user,
					organization: Object.keys(organization).length > 0 ? organization : undefined,
					date: new Date(),
					activity: activityObject,
					token,
					...(referralInfo && { referral: referralInfo }),
					...values,
				}),
			})

			if (response.ok) {
				setIsOpen(false)
				if (form.redirectUrl) {
					navigate(form.redirectUrl)
				}
			}
		} catch (error) {
			console.error("Form submission error:", error)
			setIsSubmitting(false)
		}
	}

	const modalProps = {
		open: isOpen,
		onClose: () => setIsOpen(false),
		className: "relative z-50",
	}

	return (
		<Dialog {...modalProps}>
			<div
				className="fixed inset-0 bg-black/30"
				aria-hidden="true"
			/>
			<div className="fixed inset-0 flex items-center justify-center p-4">
				<Dialog.Panel className="mx-auto w-full max-w-xl rounded bg-white relative">
					<div className="flex items-center justify-between border-b border-gray-200 p-4">
						<Dialog.Title className="text-lg font-semibold text-gray-900">
							{form.title}
						</Dialog.Title>
						<button
							type="button"
							className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
							onClick={() => setIsOpen(false)}
						>
							<span className="sr-only">Close</span>
							<XIcon
								className="h-6 w-6"
								aria-hidden="true"
							/>
						</button>
					</div>
					<div className="max-h-[calc(100vh-10rem)] overflow-y-auto p-6">
						{form.description && (
							<div className="text-gray-600 mb-6">
								<RichText description={form.description} />
							</div>
						)}
						<DynamicForm
							form={form}
							onSubmit={handleSubmit}
							className="space-y-6"
							autoFocus={true}
							disabled={isSubmitting}
						/>

						<ReCAPTCHA
							sitekey={process.env.GATSBY_RECAPTCHA_SITE_KEY}
							size="invisible"
							ref={reRef}
						/>
					</div>
				</Dialog.Panel>
			</div>
		</Dialog>
	)
}
